import { RootState } from '@/app/store';
import { isData } from '@/shared/helpers/websocket';
import { socket, waitForOpenConnection } from '@/shared/websocket/socket';
import { AssetBalance, AssetBalances } from '@/types/balances';
import { SocketChannelName, camelizeNestedKeys } from '@cex/shared-modules';
import { EntityState, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { mainEmptySplitApi } from '@/features/main/mainApi';

const balanceAdapter = createEntityAdapter<AssetBalance>({
  selectId: (balance) => balance.assetId
});

const initialState = balanceAdapter.getInitialState();

export const balancesApi = mainEmptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getPortfolioBalances: builder.query<EntityState<AssetBalance>, void>({
      query: () => '/balance',
      transformResponse(response: AssetBalances) {
        const camelizedResponse = camelizeNestedKeys(response);

        // we don't want to include the Euro balance if there is one
        return balanceAdapter.setAll(
          initialState,
          camelizedResponse.filter((r) => r.assetId !== 'eur')
        );
      },
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        // Websocket implementation: https://redux-toolkit.js.org/rtk-query/usage/streaming-updates#websocket-chat-api
        try {
          await cacheDataLoaded;
          await waitForOpenConnection();

          socket.subscribe(SocketChannelName.BALANCE);

          const listener = (event: MessageEvent) => {
            const data = JSON.parse(event.data);
            if (data.chan_name !== SocketChannelName.BALANCE || !isData(data)) {
              return;
            }

            updateCachedData((draft) => {
              balanceAdapter.upsertOne(draft, camelizeNestedKeys(data.data));
            });
          };
          socket.addEventListener('message', listener);
        } catch (e) {
          console.error(e);
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        socket.close();
      },
      providesTags: (result) =>
        result ? [...result.ids.map((id) => ({ type: 'Balances' as const, id })), 'Balances'] : ['Balances']
    })
  })
});

// @ts-ignore
const selectPortfolioBalancesResult = balancesApi.endpoints.getPortfolioBalances.select();

export const selectPortfolioBalancesData = createSelector(selectPortfolioBalancesResult, (apiSlice) => apiSlice.data);

export const { selectAll: selectAllPortfolioBalances, selectById: selectPortfolioBalanceById } =
  balanceAdapter.getSelectors((state: RootState) => selectPortfolioBalancesData(state) ?? initialState);

export const { useGetPortfolioBalancesQuery } = balancesApi;
